import { useEffect } from 'react'
import type { StorageAnswer } from '../../commands'
import { canDivert } from '../../model/delivery'
import {
  Answer,
  Btn,
  Card,
  Cta,
  Footer,
  Icon,
  Row,
  SpeakButton,
  Strip,
  Tile,
  V1Screen,
  WaitCta,
} from '../kit'
import { C } from '../style'
import type { V1IconName } from '../sprite'
import { cropName, fmtDate, tr } from '../text'
import type { RosterFarmer } from '@/offline/roster'
import { areaText, buyableCultivations } from './cultivations'
import { Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'

const CROP_ICON: Record<string, V1IconName> = { moong: 'moong', groundnut: 'groundnut' }

function useNeedsMoisture() {
  const { roster } = useV1()
  return (cropId: string) =>
    roster.config.crops.find((c) => c.crop_id === cropId)?.moisture_required ?? false
}

/** Picks one cultivation: its crop, variety and moisture rule go with the delivery. */
function usePickCultivation() {
  const { dispatch } = useV1()
  const needs = useNeedsMoisture()
  return (c: RosterFarmer['cultivations'][number]) =>
    dispatch({
      type: 'details',
      patch: {
        crop_id: c.crop_id,
        cultivation_id: c.id,
        crop_variety_id: c.crop_variety_id ?? null,
        another_field: false,
        delivered_by: { kind: 'SELF' },
        moisture_required: needs(c.crop_id),
      },
    })
}

/** F4.2 — खेत चुनें: one card per ready cultivation (crop, farm, plot); one per delivery. */
export function CropScreen({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { d, roster } = useV1()
  const options = buyableCultivations(roster, d.farmer.id)
  const pick = usePickCultivation()
  const only = options.length === 1 ? options[0]! : null
  // His only cultivation is picked for her (she still sees it and taps आगे).
  useEffect(() => {
    if (!d.cultivation_id && !d.crop_id && only) pick(only)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the screen opens
  }, [])
  return (
    <V1Screen
      main={{ gap: 10 }}
      header={
        <StepHeader step="details" icon="user" title={tr('gate.v1.crop.title')} onBack={onBack} />
      }
      footer={
        <Footer>
          {d.crop_id ? (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={onNext} />
          ) : (
            <WaitCta>{tr('gate.v1.crop.title')}</WaitCta>
          )}
        </Footer>
      }
    >
      {options.length === 0 && <Strip icon="bang">{tr('gate.v1.crop.none')}</Strip>}
      {options.map((c) => {
        const area = areaText(c.area, c.area_unit)
        const place = [c.farm_name, c.plot_label].filter(Boolean).join(' · ')
        return (
          <Tile
            key={c.id}
            pressed={d.cultivation_id === c.id}
            onClick={() => pick(c)}
            style={{
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 10,
              width: '100%',
              textAlign: 'left',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 8,
                background: C.photo,
                color: C.photoInk,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={CROP_ICON[c.crop_id] ?? 'sack'} size={36} sw={1.4} />
            </span>
            <span
              style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}
            >
              <b style={{ fontSize: 19 }}>
                {cropName(roster.config.crops, c.crop_id)}
                {c.variety_name ? (
                  <span style={{ fontWeight: 500, color: C.muted }}> · {c.variety_name}</span>
                ) : null}
              </b>
              {place && <span style={{ fontSize: 16 }}>{place}</span>}
              {(area || c.start_date) && (
                <span style={{ fontSize: 15, color: C.muted }}>
                  {[
                    area,
                    c.start_date
                      ? tr('gate.v1.crop.sown', { date: fmtDate(new Date(c.start_date)) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              )}
            </span>
          </Tile>
        )
      })}
      <Strip icon="sack" iconSize={24}>
        {tr('gate.v1.crop.twoCrops')}
      </Strip>
    </V1Screen>
  )
}

const ANSWERS: StorageAnswer[] = ['NO', 'DONT_KNOW', 'YES']

function Question({
  field,
  q,
  hint,
}: {
  field: 'stored_over_7_days' | 'tablet_or_powder'
  q: string
  hint?: string
}) {
  const { d, dispatch, stamp } = useV1()
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25, color: C.ink }}>{q}</span>
          {hint && <span style={{ fontSize: 16, color: C.muted, lineHeight: 1.3 }}>{hint}</span>}
        </span>
        <SpeakButton text={hint ? `${q} ${hint}` : q} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
        {ANSWERS.map((a) => (
          <Answer
            key={a}
            pressed={d.storage[field] === a}
            onClick={() => dispatch({ type: 'storage', field, value: a, at: stamp() })}
          >
            {tr(`gate.v1.answer.${a}`)}
          </Answer>
        ))}
      </div>
    </Card>
  )
}

/** F4.2 — 2 सवाल: the two home-storage questions; दिन पर सुखाएँगे before any tag. */
export function StorageScreen({
  onBack,
  onNext,
  onDry,
}: {
  onBack: () => void
  onNext: () => void
  onDry: () => void
}) {
  const { d } = useV1()
  const left = [d.storage.stored_over_7_days, d.storage.tablet_or_powder].filter(
    (a) => a === null,
  ).length
  return (
    <V1Screen
      header={
        <StepHeader
          step="details"
          icon="user"
          title={tr('gate.v1.storage.title')}
          onBack={onBack}
        />
      }
      footer={
        <Footer>
          {/* Tasks run in any order: dry at home only while nothing is tagged or sealed. */}
          {canDivert(d) && (
            <Row>
              <Btn icon="sun" onClick={onDry}>
                {tr('gate.v1.dry.button')}
              </Btn>
            </Row>
          )}
          {left > 0 ? (
            <WaitCta>{tr('gate.v1.storage.left', { count: left })}</WaitCta>
          ) : (
            <Cta label={tr('gate.v1.next')} icon="next" onClick={onNext} />
          )}
        </Footer>
      }
    >
      <Question field="stored_over_7_days" q={tr('gate.v1.storage.q1')} />
      <Question
        field="tablet_or_powder"
        q={tr('gate.v1.storage.q2')}
        hint={tr('gate.v1.storage.q2hint')}
      />
    </V1Screen>
  )
}

/** F4-B1 — घर पर सुखाएँगे (before any tag): bring it later at that day's price. */
export function DivertScreen({ onBack, onConfirm }: { onBack: () => void; onConfirm: () => void }) {
  const { busy } = useV1()
  const steps = [tr('gate.v1.dry.card'), tr('gate.v1.dry.bags')]
  return (
    <V1Screen
      main={{ gap: 14 }}
      header={
        <StepHeader step="details" icon="sun" title={tr('gate.v1.dry.title')} onBack={onBack} />
      }
      footer={
        <Footer>
          <Row>
            <Btn icon="back" onClick={onBack}>
              {tr('gate.v1.dry.sellToday')}
            </Btn>
          </Row>
          <Cta label={tr('gate.v1.dry.confirm')} icon="sun" busy={busy} onClick={onConfirm} />
        </Footer>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          paddingTop: 16,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            background: C.warnWash,
            color: C.accDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="sun" size={76} sw={1.6} />
        </span>
        <b style={{ fontSize: 26, lineHeight: 1.2 }}>{tr('gate.v1.dry.later')}</b>
        <span style={{ fontSize: 20 }}>{tr('gate.v1.dry.price')}</span>
      </div>
      <Card>
        {steps.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 14,
                border: `2px solid ${C.ink}`,
                color: C.ink,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1,
                boxSizing: 'border-box',
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontSize: 17, lineHeight: 1.3 }}>
              <Rich text={s} />
            </span>
          </div>
        ))}
      </Card>
    </V1Screen>
  )
}
